import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";

export default function AdminRoles() {
  const client = adminClient();
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([client.get("/admin/roles"), client.get("/admin/permissions")])
      .then(([r, p]) => {
        setRoles(r.data.roles);
        setPerms(p.data.permissions);
        const d = {}; r.data.roles.forEach((role) => { d[role.name] = new Set(role.permissions); });
        setDraft(d);
      }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = (roleName, perm) => {
    if (roleName === "super_admin") return;
    setDraft((prev) => {
      const s = new Set(prev[roleName]);
      s.has(perm) ? s.delete(perm) : s.add(perm);
      return { ...prev, [roleName]: s };
    });
  };

  const save = async (roleName) => {
    try { await client.put(`/admin/roles/${roleName}`, { permissions: [...draft[roleName]] }); toast.success(`${roleName} permissions saved`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-taven-gold" />;

  return (
    <div data-testid="admin-roles">
      <h2 className="text-xl font-black font-display mb-2">Roles &amp; Permission Matrix</h2>
      <p className="text-sm text-muted-foreground mb-5">Toggle permissions per role. Super Admin always has full access and cannot be changed.</p>
      <div className="overflow-x-auto bg-white border border-black/10">
        <table className="text-sm min-w-[800px]">
          <thead className="bg-taven-gray/60">
            <tr>
              <th className="text-left px-3 py-2 font-bold uppercase text-[10px] tracking-wider sticky left-0 bg-taven-gray/60">Permission</th>
              {roles.map((r) => <th key={r.name} className="px-3 py-2 font-bold text-[10px] uppercase tracking-wider whitespace-nowrap">{r.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {perms.map((p) => (
              <tr key={p.key} className="border-t border-black/5">
                <td className="px-3 py-2 font-medium sticky left-0 bg-white whitespace-nowrap">{p.label}</td>
                {roles.map((r) => {
                  const on = draft[r.name]?.has(p.key);
                  const locked = r.name === "super_admin";
                  return (
                    <td key={r.name} className="px-3 py-2 text-center">
                      <button disabled={locked} onClick={() => toggle(r.name, p.key)} data-testid={`perm-${r.name}-${p.key}`}
                        className={`w-6 h-6 border flex items-center justify-center mx-auto ${on ? "bg-taven-orange border-taven-orange text-white" : "border-black/20"} ${locked ? "opacity-60 cursor-not-allowed" : ""}`}>
                        {on && <Check className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="border-t border-black/10 bg-taven-gray/30">
              <td className="px-3 py-2 sticky left-0 bg-taven-gray/30" />
              {roles.map((r) => (
                <td key={r.name} className="px-3 py-2 text-center">
                  {r.name !== "super_admin" && <button onClick={() => save(r.name)} data-testid={`save-role-${r.name}`} className="text-[10px] font-bold uppercase bg-taven-black text-white px-3 py-1.5 hover:bg-taven-orange">Save</button>}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
